/**
 * PII / secret redaction for log payloads.
 *
 * The production logger (`monitoring/logger.ts`) pipes any `meta`
 * object through `redact()` before emitting JSON. The goal is to
 * keep accidental secrets out of DataDog / Logtail / our own
 * `ingest-error-report` edge function.
 *
 * Rules:
 *   1. Keys matching the deny-list have their value replaced with
 *      the string `'[REDACTED]'`. Matching is case-insensitive on
 *      the key name.
 *   2. String values that look like a bearer token, a Supabase JWT,
 *      or an API-key-ish blob are redacted wherever they appear.
 *   3. Objects are recursed; arrays are mapped; primitives pass
 *      through unchanged. Depth is capped to defeat cycles.
 *
 * The function is pure and does not mutate its input — callers can
 * keep logging the original object for their own purposes.
 */

const DENY_KEYS = new Set([
    // Auth / sessions
    'password',
    'passwd',
    'pass',
    'secret',
    'token',
    'access_token',
    'refresh_token',
    'id_token',
    'authorization',
    'cookie',
    'set-cookie',
    'session',
    'sessionid',
    'jwt',

    // API keys (generic + vendor)
    'api_key',
    'apikey',
    'api-key',
    'x-api-key',
    'client_secret',
    'client-secret',
    'private_key',
    'privatekey',
    'encryption_key',

    // Vendor-specific
    'openai_api_key',
    'anthropic_api_key',
    'google_api_key',
    'plaid_secret',
    'plaid_client_id',
    'coinbase_api_key',
    'coinbase_private_key',
    'supabase_service_role_key',
    'stripe_secret_key',
    'datadog_api_key',
    'logtail_source_token',

    // PII
    'ssn',
    'social_security_number',
    'credit_card',
    'creditcard',
    'card_number',
    'cvv',
    'cvc',
])

// Values that look like secrets even when their parent key doesn't
// obviously scream "I'm a secret". Conservative on false positives —
// each pattern has a distinctive prefix or structure.
const VALUE_PATTERNS: Array<{ re: RegExp; replace: string }> = [
    // Bearer tokens in Authorization-style strings
    { re: /Bearer\s+[A-Za-z0-9._\-+/=]{16,}/gi, replace: 'Bearer [REDACTED]' },
    // Supabase / JWT (three dot-separated base64url segments, 20+ chars each)
    { re: /eyJ[A-Za-z0-9_\-]{10,}\.[A-Za-z0-9_\-]{10,}\.[A-Za-z0-9_\-]{10,}/g, replace: '[REDACTED_JWT]' },
    // OpenAI keys (`sk-` + 32+ chars)
    { re: /\bsk-[A-Za-z0-9_\-]{20,}\b/g, replace: '[REDACTED_SK]' },
    // Anthropic keys (`sk-ant-` prefix)
    { re: /\bsk-ant-[A-Za-z0-9_\-]{20,}\b/g, replace: '[REDACTED_ANTHROPIC_KEY]' },
    // Stripe keys (rk_/sk_/pk_ live/test)
    { re: /\b(rk|sk|pk)_(live|test)_[A-Za-z0-9]{16,}\b/g, replace: '[REDACTED_STRIPE_KEY]' },
]

const MAX_DEPTH = 8

export function redact<T>(value: T): T {
    return redactInner(value, 0) as T
}

function redactInner(value: unknown, depth: number): unknown {
    if (depth > MAX_DEPTH) return '[REDACTED_DEPTH]'
    if (value === null || value === undefined) return value

    const t = typeof value
    if (t === 'string') return redactString(value as string)
    if (t === 'number' || t === 'boolean' || t === 'bigint') return value

    if (value instanceof Error) {
        // Preserve stack + message but scrub message for embedded tokens.
        return {
            name: value.name,
            message: redactString(value.message),
            stack: value.stack,
        }
    }

    if (Array.isArray(value)) {
        return value.map((v) => redactInner(v, depth + 1))
    }

    if (t === 'object') {
        const out: Record<string, unknown> = {}
        for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
            if (DENY_KEYS.has(k.toLowerCase())) {
                out[k] = '[REDACTED]'
            } else {
                out[k] = redactInner(v, depth + 1)
            }
        }
        return out
    }

    // functions, symbols, etc. — drop to a stable string
    return String(value)
}

function redactString(s: string): string {
    let out = s
    for (const { re, replace } of VALUE_PATTERNS) {
        out = out.replace(re, replace)
    }
    return out
}
